import { useEffect, useRef, type RefObject } from 'react';
import { useParams } from 'react-router-dom';
import { VncScreen, type VncScreenHandle } from 'react-vnc';
import Guacamole, { type Client as GuacamoleClient } from 'guacamole-common-js';
import { Keyboard, Terminal, RefreshCw, X, ClipboardPaste } from 'lucide-react';
import { Button, Spinner } from '@vnetwork/ui/primitives';
import { useInstanceRemoteTicket, type RemoteTicket } from '@/api/opennebula';

/**
 * Bare full-window in-browser console (rendered outside AppLayout, opened in a
 * popup). OpenNebula's `/instances/:id/vnc` ticket gives `{ url, token, client }`:
 * `client === 'Guacamole'` → Guacamole tunnel; otherwise noVNC (Sunstone).
 *
 * NOTE: ported from the legacy console; the live VNC/RDP stream can't be verified
 * headlessly — confirm against a powered-on VM.
 */
export function InstanceConsole() {
  const { serviceId, instanceId } = useParams<{ serviceId: string; instanceId: string }>();
  const { data: ticket, isLoading } = useInstanceRemoteTicket(serviceId, instanceId);

  const name = ticket?.instance?.name || instanceId || 'Console';
  const url = ticket?.url || '';
  const isGuacamole = ticket?.client === 'Guacamole';

  if (isLoading) {
    return (
      <Shell name={name}>
        <Centered>
          <Spinner /> <span style={{ marginLeft: 8 }}>Acquiring console…</span>
        </Centered>
      </Shell>
    );
  }
  if (!url) {
    return (
      <Shell name={name}>
        <Centered>
          Console is unavailable. The instance may be powered off or still starting.
        </Centered>
      </Shell>
    );
  }

  return isGuacamole ? <GuacamoleView name={name} ticket={ticket!} /> : <NoVncView name={name} url={url} />;
}

/**
 * Both noVNC (react-vnc) and Guacamole render their framebuffer <canvas> with an
 * inline `z-index: -1` (re-applied on resize), so it hides behind the console's
 * black background while the cursor layer stays visible. The inline value can't be
 * removed from outside, so force any such canvas back above with inline
 * `!important` and re-apply whenever the library rewrites the style. Only canvases
 * currently at -1 are touched, so Guacamole's other layers keep their order.
 */
function useLiftCanvasZIndex(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const lift = () => {
      root.querySelectorAll('canvas').forEach((c) => {
        if ((c as HTMLCanvasElement).style.getPropertyValue('z-index') === '-1') {
          (c as HTMLCanvasElement).style.setProperty('z-index', '1', 'important');
        }
      });
    };
    lift();
    const obs = new MutationObserver(lift);
    obs.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    return () => obs.disconnect();
  }, [ref]);
}

// ---- noVNC (Sunstone) ----------------------------------------------------

function NoVncView({ name, url }: { name: string; url: string }) {
  const ref = useRef<VncScreenHandle>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  useLiftCanvasZIndex(stageRef);

  // Paste the host clipboard into the guest (noVNC clipboardPasteFrom).
  const paste = async () => {
    try {
      const text = await navigator.clipboard?.readText();
      if (!text) return;
      const h = ref.current as unknown as {
        clipboardPaste?: (t: string) => void;
        rfb?: { clipboardPasteFrom?: (t: string) => void };
      } | null;
      h?.clipboardPaste?.(text) ?? h?.rfb?.clipboardPasteFrom?.(text);
    } catch {
      /* clipboard blocked */
    }
  };
  return (
    <Shell
      name={name}
      onCtrlAltDel={() => ref.current?.sendCtrlAltDel?.()}
      onReconnect={() => ref.current?.connect?.()}
      onPaste={paste}
    >
      <div ref={stageRef} style={{ position: 'relative', flex: 1, minHeight: 0, background: '#000', overflow: 'hidden' }}>
        <VncScreen
          ref={ref}
          url={url}
          scaleViewport
          background="#000"
          style={{ width: '100%', height: '100%' }}
          loadingUI={<Centered light>Connecting…</Centered>}
        />
      </div>
    </Shell>
  );
}

// ---- Guacamole -----------------------------------------------------------

// X11 keysyms for Ctrl+Alt+Del.
const KEY_CTRL = 0xffe3;
const KEY_ALT = 0xffe9;
const KEY_DELETE = 0xffff;

function GuacamoleView({ name, ticket }: { name: string; ticket: RemoteTicket }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const clientRef = useRef<GuacamoleClient | null>(null);
  useLiftCanvasZIndex(containerRef);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !ticket.url) return;

    const tunnel = new Guacamole.WebSocketTunnel(ticket.url);
    const client = new Guacamole.Client(tunnel);
    clientRef.current = client;

    // `onresize` isn't in the guacamole-common-js display typings but exists at runtime.
    const guacDisplay = client.getDisplay() as unknown as {
      getElement(): HTMLElement;
      onresize?: (width: number, height: number) => void;
    };
    const display = guacDisplay.getElement();
    container.appendChild(display);

    // Size the display element to the remote resolution the server reports.
    // Without this the element stays 0×0 and the canvas renders into nothing —
    // the tunnel connects but the screen shows black.
    guacDisplay.onresize = (width, height) => {
      display.style.width = `${width}px`;
      display.style.height = `${height}px`;
    };

    const keyboard = new Guacamole.Keyboard(document);
    // Return false so Guacamole calls preventDefault() — otherwise the browser's
    // default action for Enter/Tab/Space/Backspace fires and the keystroke never
    // reaches the guest (e.g. Enter can't submit a login).
    keyboard.onkeydown = (sym) => { client.sendKeyEvent(1, sym); return false; };
    keyboard.onkeyup = (sym) => { client.sendKeyEvent(0, sym); return false; };

    const mouse = new Guacamole.Mouse(display);
    mouse.onmousedown = mouse.onmouseup = mouse.onmousemove = (state) => client.sendMouseState(state);

    try {
      // Guacamole parses a query-string of connection params, not a bare token.
      client.connect(`token=${ticket.token || ''}`);
    } catch {
      /* surfaced via onerror */
    }

    return () => {
      keyboard.onkeydown = null;
      keyboard.onkeyup = null;
      try {
        client.disconnect();
      } catch {
        /* ignore */
      }
      if (display.parentNode === container) container.removeChild(display);
    };
  }, [ticket.url, ticket.token]);

  const sendCtrlAltDel = () => {
    const c = clientRef.current;
    if (!c) return;
    [KEY_CTRL, KEY_ALT, KEY_DELETE].forEach((k) => c.sendKeyEvent(1, k));
    [KEY_DELETE, KEY_ALT, KEY_CTRL].forEach((k) => c.sendKeyEvent(0, k));
  };

  return (
    <Shell name={name} onCtrlAltDel={sendCtrlAltDel}>
      <div
        ref={containerRef}
        style={{ flex: 1, minHeight: 0, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}
      />
    </Shell>
  );
}

// ---- Shared chrome -------------------------------------------------------

function Shell({
  name,
  children,
  onCtrlAltDel,
  onReconnect,
  onPaste,
}: {
  name: string;
  children: React.ReactNode;
  onCtrlAltDel?: () => void;
  onReconnect?: () => void;
  onPaste?: () => void;
}) {
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--vn-bg-canvas)' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--vn-space-3)',
          padding: 'var(--vn-space-2) var(--vn-space-4)',
          borderBottom: '1px solid var(--vn-border-default)',
          background: 'var(--vn-bg-surface)',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
          <Terminal size={16} /> {name}
        </span>
        <div style={{ display: 'flex', gap: 'var(--vn-space-2)' }}>
          {onCtrlAltDel && (
            <Button size="sm" variant="secondary" startIcon={<Keyboard size={15} />} onClick={onCtrlAltDel}>
              Ctrl+Alt+Del
            </Button>
          )}
          {onPaste && (
            <Button size="sm" variant="secondary" startIcon={<ClipboardPaste size={15} />} onClick={onPaste}>
              Paste
            </Button>
          )}
          {onReconnect && (
            <Button size="sm" variant="secondary" startIcon={<RefreshCw size={15} />} onClick={onReconnect}>
              Reconnect
            </Button>
          )}
          <Button size="sm" variant="ghost" startIcon={<X size={15} />} onClick={() => window.close()}>
            Close
          </Button>
        </div>
      </header>
      {children}
    </div>
  );
}

function Centered({ children, light }: { children: React.ReactNode; light?: boolean }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: light ? '#fff' : 'var(--vn-fg-muted)',
        padding: 'var(--vn-space-6)',
      }}
    >
      {children}
    </div>
  );
}
